<script setup lang="ts">
/**
 * Эпики проекта — макет «Эпики проекта» на странице «01 · Ядро».
 *
 * Раздел был в сайдбаре с самого начала, но вёл на заглушку: разбор эпика
 * (DEV-1478) живёт на странице самого эпика, и попасть туда можно было только
 * из списка задач или поиском. Здесь — вход в этот разбор: строка на эпик с
 * готовностью по его задачам.
 *
 * Готовность приходит раскладкой по колонкам с сервера: он обходит дерево
 * эпика одним запросом. Экран считал её сам, выкачивая задачи проекта, и на
 * полутора тысячах задач терял эпики за своим же потолком (DEV-1635).
 *
 * Поиск здесь по загруженному списку, а не запросом: эпиков в проекте десятки,
 * и они уже все на руках — ходить за ними на сервер на каждую букву значило бы
 * ждать ответа там, где ответ уже есть.
 */
import { AppButton, AppTextField, useToast } from '@vinprop/design-system'
import { AlertTriangle, FolderOpen, Search, SearchX, Server } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage, errorRoute } from '@/api/errors'
import { useAuthStore } from '@/features/auth/store'
import { raiseStand } from '@/features/devops/api'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { loadEpics, type EpicRow } from '@/features/epic/api'
import { formatDueDate } from '@/features/issues/format'
import CreateIssuePanel from '@/features/issues/components/CreateIssuePanel.vue'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { getProject, listColumns, type Project, type ProjectColumn } from '@/features/projects/api'

import type { CreatedIssue } from '@/features/issues/api'
import { notifyIssueCreated } from '@/features/issues/created'

const { t, locale } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()

const auth = useAuthStore()

const projectKey = computed(() => String(route.params.key ?? ''))

const project = ref<Project | null>(null)
const columns = ref<ProjectColumn[]>([])
const epics = ref<EpicRow[]>([])
const isLoading = ref(false)
const error = ref<unknown>(null)
const search = ref('')
const isCreateOpen = ref(false)

const breadcrumbs = computed(() => [
  { label: t('nav.projects'), to: '/projects' },
  { label: project.value?.name || projectKey.value, to: `/p/${projectKey.value}/board` },
  { label: t('views.epics') },
])

const found = computed(() => {
  const needle = search.value.trim().toLowerCase()

  if (!needle) return epics.value

  return epics.value.filter(
    (row) =>
      row.issue.title.toLowerCase().includes(needle) ||
      row.issue.key.toLowerCase().includes(needle),
  )
})

/** Доля сегмента полосы; у эпика без задач полоса пустая, а не полная */
const share = (value: number, total: number) => (total > 0 ? `${(value / total) * 100}%` : '0%')

/**
 * Строка сводки — те же слова, что в разборе одного эпика: экран списка и
 * страница эпика не должны называть одно и то же по-разному. Нули пропускаем:
 * в списке из десяти строк «0 в работе · 0 открыто» повторённое десять раз
 * прячет то, ради чего в список смотрят.
 */
const summary = (row: EpicRow) => {
  const { total, done, inProgress, open } = row.progress

  if (!total) return t('epic.empty')

  const parts = [t('epic.summary.total', total), t('epic.summary.done', { count: done })]

  if (inProgress) parts.push(t('epic.summary.inProgress', { count: inProgress }))
  if (open) parts.push(t('epic.summary.open', { count: open }))

  const due = formatDueDate(row.issue.dueDate, locale.value)

  if (due) parts.push(t('epic.list.due', { date: due }))

  return parts.join(' · ')
}

const load = async () => {
  isLoading.value = true
  error.value = null

  try {
    const target = await getProject(projectKey.value)

    project.value = target
    columns.value = await listColumns(target.id)
    epics.value = await loadEpics(target.id, columns.value)
  } catch (failure) {
    error.value = failure

    // «Нет такого проекта» и «есть, но не для вас» разбирает не этот экран.
    const failureRoute = errorRoute(failure, projectKey.value)

    if (failureRoute) await router.replace(failureRoute)
  } finally {
    isLoading.value = false
  }
}

const onCreated = async (issue: CreatedIssue) => {
  notifyIssueCreated(issue.key)

  await load()
}

const open = (row: EpicRow) => {
  void router.push(`/issue/${row.issue.key}`)
}

/** Стенды поднимает не всякий: право то же, что у кнопок в карточке задачи. */
const canRaise = computed(() => auth.can('tracker:devops'))

const raising = ref('')

/**
 * Стенд эпика — один namespace с его номером, состав собирает сервер по
 * задачам эпика. Кнопка появляется, только когда весь эпик доехал до
 * тестирования: раньше собирать нечего (DEV-1633).
 */
const raise = async (row: EpicRow) => {
  raising.value = row.issue.id

  try {
    await raiseStand(row.issue.id)
    toast.success(t('epic.list.stand.started', { key: row.issue.key }))
  } catch (failure) {
    // Отказ сервера объясняет причину — «у задач эпика не указаны сервисы»,
    // и заменять её общим «не удалось» значит отправлять человека гадать.
    toast.error(errorMessage(failure, t('epic.list.stand.failed')))
  } finally {
    raising.value = ''
  }
}

onMounted(load)

// Переход между проектами из сайдбара меняет только параметр маршрута.
watch(projectKey, load)
</script>

<template>
  <TrackerToolbar :breadcrumbs="breadcrumbs">
    <template v-if="project">
      <AppTextField
        v-model="search"
        class="epics__search"
        size="small"
        :placeholder="t('epic.list.search')"
        :icon="Search"
      />
      <AppButton variant="primary" size="small" @click="isCreateOpen = true">
        {{ t('epic.list.create') }}
      </AppButton>
    </template>
  </TrackerToolbar>

  <div class="epics">
    <div v-if="isLoading && !epics.length" class="epics__list">
      <div v-for="index in 4" :key="index" class="epics__skeleton" />
    </div>

    <TrackerEmptyState
      v-else-if="error"
      :icon="AlertTriangle"
      :title="t('epic.list.error.title')"
      :description="t('epic.list.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="load">{{ t('board.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!epics.length"
      :icon="FolderOpen"
      :title="t('epic.list.empty.title')"
      :description="t('epic.list.empty.description')"
    >
      <AppButton variant="primary" @click="isCreateOpen = true">{{ t('epic.list.create') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!found.length"
      :icon="SearchX"
      :title="t('epic.list.nothingFound.title')"
      :description="t('epic.list.nothingFound.description')"
    >
      <AppButton variant="secondary" @click="search = ''">
        {{ t('toolbar.filters.reset') }}
      </AppButton>
    </TrackerEmptyState>

    <div v-else class="epics__list">
      <article
        v-for="row in found"
        :key="row.issue.id"
        class="erow"
        role="button"
        tabindex="0"
        :aria-label="t('issues.list.openIssue', { key: row.issue.key })"
        @click="open(row)"
        @keydown.enter="open(row)"
      >
        <div class="erow__head">
          <IssueTypeIcon type="ISSUE_TYPE_EPIC" />
          <span class="erow__key">{{ row.issue.key }}</span>
          <h2 class="erow__title">{{ row.issue.title }}</h2>
          <span class="erow__ratio">{{ row.progress.done }} / {{ row.progress.total }}</span>

          <!-- Нажатие на строку открывает эпик, поэтому кнопка гасит всплытие:
               иначе подъём стенда уводил бы со списка на карточку. -->
          <AppButton
            v-if="canRaise && row.isTesting"
            class="erow__stand"
            variant="secondary"
            size="small"
            :loading="raising === row.issue.id"
            :icon="Server"
            @click.stop="raise(row)"
          >
            {{ t('epic.list.stand.raise') }}
          </AppButton>
        </div>

        <!-- Полоса читается слева направо: сделанное, взятое в работу, дальше
             непочатое — тот же порядок, что в сводке эпика. -->
        <div class="erow__bar">
          <span
            class="erow__seg erow__seg--done"
            :style="{ width: share(row.progress.done, row.progress.total) }"
          />
          <span
            class="erow__seg erow__seg--doing"
            :style="{ width: share(row.progress.inProgress, row.progress.total) }"
          />
        </div>

        <p class="erow__summary">{{ summary(row) }}</p>
      </article>
    </div>
  </div>

  <CreateIssuePanel
    v-if="project"
    v-model="isCreateOpen"
    :project-id="project.id"
    :type-profile="project.typeProfile"
    default-type="ISSUE_TYPE_EPIC"
    @created="onCreated"
  />
</template>

<style scoped>
.epics {
  height: calc(100vh - 48px);
  padding: 20px 24px;
  overflow-y: auto;
}

.epics__search {
  width: 220px;
}

.epics__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.epics__skeleton {
  height: 92px;
  border-radius: var(--radius-md);
  background: var(--color-bg-muted);
}

.erow {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.erow:hover,
.erow:focus-visible {
  border-color: var(--color-primary);
}

.erow__head {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.erow__key {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-weight: var(--font-weight-medium);
}

.erow__title {
  margin: 0;
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-lg, 15px);
  font-weight: var(--font-weight-medium);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.erow__ratio {
  flex-shrink: 0;
  margin-left: auto;
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-variant-numeric: tabular-nums;
}

.erow__bar {
  display: flex;
  height: 8px;
  overflow: hidden;
  border-radius: 4px;
  background: var(--color-bg-muted);
}

.erow__seg {
  height: 100%;
  transition: width 0.2s ease;
}

.erow__seg--done {
  background: var(--color-success);
}

.erow__seg--doing {
  background: var(--color-status-in-progress);
}

.erow__summary {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
}

@media (prefers-reduced-motion: reduce) {
  .erow__seg {
    transition: none;
  }
}
</style>
